/** Competition view — hub for navigating to Producer, Judge, Setup, OBS link. */

import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useCompetitionStore } from "@/stores/competition";
import { useAuthStore } from "@/stores/auth";
import { Button } from "@/components/ui/Button";

const ROLE_COLORS: Record<string, string> = {
  admin: "#3b82f6",
  judge: "#22c55e",
  producer: "#a855f7",
};

export function CompetitionViewPage() {
  const { competitionId } = useParams<{ competitionId: string }>();
  const user = useAuthStore((s) => s.user);
  const { competition, members, liveState, loadCompetition, loadMembers, loadLiveState } = useCompetitionStore();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!competitionId) return;
    Promise.all([loadCompetition(competitionId), loadMembers(competitionId), loadLiveState(competitionId)]).then(
      () => setLoading(false),
    );
  }, [competitionId, loadCompetition, loadMembers, loadLiveState]);

  if (loading || !competition) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  const myMember = members.find((m) => m.user_id === user?.id);
  const myRoles = myMember?.roles ?? [];
  const isOwner = competition.owner_id === user?.id;
  const isAdmin = isOwner || myRoles.includes("admin");
  const isJudge = myRoles.includes("judge");
  const isProducer = myRoles.includes("producer");
  const obsUrl = `${window.location.origin}/obs/${competition.obs_token}`;

  // Find the owner in the members list for display
  const ownerMember = members.find((m) => m.user_id === competition.owner_id);
  const ownerName = ownerMember?.user.display_name ?? ownerMember?.user.email;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{competition.name}</h1>
          <p className="page-subtitle">
            {!isOwner && ownerName && (
              <span style={{ marginRight: 8 }}>{ownerName}'s competition</span>
            )}
            {isOwner && (
              <span style={{ marginRight: 8 }}>{competition.description ?? "Competition"}</span>
            )}
            {!isOwner && competition.description && (
              <span style={{ marginRight: 8 }}>&middot; {competition.description}</span>
            )}
            {liveState?.is_live && <span className="badge badge-live" style={{ marginLeft: 4 }}>LIVE</span>}
          </p>
          {/* Role badges */}
          <div className="flex gap-2" style={{ marginTop: 6 }}>
            {myRoles.map((role) => (
              <span
                key={role}
                className="badge"
                style={{
                  background: ROLE_COLORS[role] ?? "var(--c-surface-2)",
                  color: "#fff",
                  textTransform: "capitalize",
                  fontSize: 11,
                }}
              >
                {role}
              </span>
            ))}
            {isOwner && !myRoles.includes("admin") && (
              <span className="badge" style={{ background: "#3b82f6", color: "#fff", fontSize: 11 }}>Owner</span>
            )}
          </div>
        </div>
      </div>

      <div className="grid-3">
        {/* Setup — show for admin */}
        {isAdmin && (
          <Link to={`/competition/${competitionId}/setup`} className="card" style={{ textDecoration: "none", color: "inherit" }}>
            <div className="card-title">Setup</div>
            <p className="text-sm text-muted" style={{ marginTop: 8 }}>
              Configure divisions, heats, assign riders, and invite your team.
            </p>
            <div style={{ marginTop: 12 }}>
              <span className="badge" style={{ background: "#3b82f6", color: "#fff" }}>Admin</span>
            </div>
          </Link>
        )}

        {/* Judge — show for judge or admin */}
        {(isJudge || isAdmin) && (
          <Link to={`/competition/${competitionId}/judge`} className="card" style={{ textDecoration: "none", color: "inherit" }}>
            <div className="card-title">Judge</div>
            <p className="text-sm text-muted" style={{ marginTop: 8 }}>
              {isJudge && myMember?.judge_number
                ? `You are Judge #${myMember.judge_number}. Score riders in real-time.`
                : "Score riders in real-time with 0.1 precision."}
            </p>
            <div style={{ marginTop: 12 }}>
              <span className="badge" style={{ background: "#22c55e", color: "#fff" }}>Access</span>
            </div>
          </Link>
        )}

        {/* Producer — show for admin or producer */}
        {(isAdmin || isProducer) && (
          <Link to={`/competition/${competitionId}/producer`} className="card" style={{ textDecoration: "none", color: "inherit" }}>
            <div className="card-title">Producer</div>
            <p className="text-sm text-muted" style={{ marginTop: 8 }}>
              Control the live overlay. Focus riders, show scores, leaderboard. Go live or dark.
            </p>
            <div style={{ marginTop: 12 }}>
              <span className="badge" style={{ background: "#a855f7", color: "#fff" }}>Access</span>
            </div>
          </Link>
        )}

        {/* OBS Link — show for admin or producer */}
        {(isAdmin || isProducer) && (
          <div className="card">
            <div className="card-title">OBS Overlay</div>
            <p className="text-sm text-muted" style={{ marginTop: 8 }}>
              Add this URL as a Browser Source in OBS (1920x1080). No login required.
            </p>
            <div style={{ marginTop: 12 }}>
              <code
                className="text-mono text-sm"
                style={{
                  display: "block",
                  padding: "8px",
                  background: "var(--c-surface-2)",
                  border: "1px solid var(--c-border)",
                  wordBreak: "break-all",
                  fontSize: "11px",
                }}
              >
                {obsUrl}
              </code>
              <div className="flex gap-2" style={{ marginTop: 8 }}>
                <Link to={`/obs/${competition.obs_token}`} target="_blank" style={{ textDecoration: "none" }}>
                  <Button variant="secondary" size="sm">
                    Open
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigator.clipboard.writeText(obsUrl)}
                >
                  Copy URL
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Members */}
      <div style={{ marginTop: 32 }}>
        <div className="card-title" style={{ marginBottom: 12 }}>
          Team ({members.length})
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Roles</th>
              <th>Judge #</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id}>
                <td>{m.user.display_name ?? "-"}</td>
                <td className="text-mono text-sm">{m.user.email}</td>
                <td>
                  <div className="flex gap-2">
                    {m.roles.map((role) => (
                      <span
                        key={role}
                        className="badge"
                        style={{
                          background: ROLE_COLORS[role] ?? "var(--c-surface-2)",
                          color: "#fff",
                          textTransform: "capitalize",
                          fontSize: 11,
                        }}
                      >
                        {role}
                      </span>
                    ))}
                  </div>
                </td>
                <td>{m.judge_number ?? "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  );
}
